import type { VoucherType } from './vouchers/types'
import { PaymentMethod } from '@/scopes/common/domain/payment-methods/payment-methods'

export const AmountTypeValues = {
  FULL: 'full',
  DEPOSIT: 'deposit',
  MIN_DEPOSIT: 'minDeposit',
} as const

export type AmountType = (typeof AmountTypeValues)[keyof typeof AmountTypeValues]

export const PaymentScopePageValues = {
  BOOKING_PAYMENT: 'BookingPayment',
  MANDATORY_PAYMENT: 'MandatoryPayment',
  PAYMENT: 'Payment',
} as const

export type PaymentScopePage = (typeof PaymentScopePageValues)[keyof typeof PaymentScopePageValues]

export const RebookingPaymentStatusValues = {
  FULLY_COVERED: 'fullyCovered',
  DOWN_PAYMENT_COVERED: 'downPaymentCovered',
  PARTIALLY_COVERED: 'partiallyCovered',
  NOT_PAID: 'notPaid',
} as const

export type RebookingPaymentStatus = (typeof RebookingPaymentStatusValues)[keyof typeof RebookingPaymentStatusValues]

export const TransactionTypeValues = {
  ONLINE: 'online',
  OFFLINE: 'offline',
  VOUCHER: 'voucher',
} as const

export type TransactionType = (typeof TransactionTypeValues)[keyof typeof TransactionTypeValues]

export interface PaymentMethodOption {
  paymentMethodType: PaymentMethod
  transactionType: TransactionType
  groupCode: string
  percentageFee?: number
  fixedFee?: number
}

export interface PaymentAmount {
  type: AmountType
  amount: number
  availablePaymentMethodTypes: PaymentMethod[]
  allowsCustomAmount?: boolean
  customPartialMinAmount?: number
  availablePaymentMethodTypesCustomPartial?: PaymentMethod[]
  amountWithFees?: AmountWithFee[]
  // false when the deposit entry only exists because of CustomPartial (no standalone Partial option)
  isDownPayment?: boolean
}

export interface AmountWithFee {
  paymentMethodType: string
  amount: number
}

export interface PaymentOptions {
  amountAlreadyPaid: number | null
  downPaymentAlreadyCovered: boolean
  paymentProvider: string
  partialMaxAmount: number
  nextPayments?: {
    fullPaymentDueDate: string
    downPaymentDueDate: string
  }
  paymentAmounts: PaymentAmount[]
  paymentMethods: PaymentMethodOption[]
  vouchers?: VoucherType[]
}

export const PaymentLoadStatusValues = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
} as const

export type PaymentLoadStatus = (typeof PaymentLoadStatusValues)[keyof typeof PaymentLoadStatusValues]

export interface PaymentState {
  paymentOptions: PaymentOptions | null
  selectedAmountType: AmountType | ''
  selectedPaymentMethodType: PaymentMethod | null
  customDepositAmount: number | null
  loadStatus: PaymentLoadStatus
  status: AdyenPaymentStatus
  paymentResult: PaymentResult | null
  successUrl: string | null
}

export interface PaymentStore extends PaymentState {
  initialize: (state: PaymentState) => void
  setIn: <K extends keyof PaymentState>(path: K, value: PaymentState[K]) => void
}

export const AdyenPaymentStatusValues = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  SUBMITTING: 'submitting',
} as const

export type AdyenPaymentStatus = (typeof AdyenPaymentStatusValues)[keyof typeof AdyenPaymentStatusValues]

export const PaymentResultValues = {
  OK: 'ok',
  PENDING: 'pending',
  DECLINE: 'decline',
  EXCEPTION: 'exception',
} as const

export type PaymentResult = (typeof PaymentResultValues)[keyof typeof PaymentResultValues]

export const CREDIT_CARD_PAYMENT_METHOD_KEY = 'creditcard'
export const ADYEN_CREDIT_CARD_GROUP_CODE = 'scheme'
export const ADYEN_CARD_CONFIG_KEY = 'card'
